/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { ReactNode } from 'react';

import { Badge } from '../Badge';
import { DocumentVersionState, DocumentVersionType, PageType } from '../../../algorithm/types';
import { IconsForDocumentFlags } from '../../../documentVersionFlags/documentVersionFlags_utils';
import { isDocumentVersionInternalOnly } from '../../../documentVersionFlags/documentVersionFlags';
import { documentVersionStateToStr } from '../../../utils';
import styles from './InnerMessageHeader.module.scss';
import { useAppSelector } from '../../../../../app/hooks';
import { selectPearlUserInfo } from '../../../Login/userSlice';
import { documentVersionLibraryToDetails } from '../../../libraryUtils';
import { clsx } from 'clsx';

interface InnerMessageHeaderProps {
  isFlipped: boolean;
  documentVersion?: DocumentVersionType;
  tooltipComponent?: ReactNode;
  isRoot?: boolean; // Whether this is the first page in an algorithm or a Guide
  page?: PageType;
  headerChrome?: ReactNode;
}

/** Display a badge for the document version, including things like:
 *
 * - Icons for flags (deprecated, contextualized, etc)
 * - Title
 * - Library badge
 * - Governance Model badge
 * - State badge (Draft, Published, etc)
 *
 * Note most of the information is not shown when isRoot is false (i.e. pages after the first page in an algorithm)
 */
export function InnerMessageHeader({
  isFlipped,
  documentVersion,
  tooltipComponent,
  isRoot,
  page: _page,
  headerChrome,
}: InnerMessageHeaderProps) {
  if (!documentVersion) return null;
  const libraryBadge = documentVersionLibraryToDetails(documentVersion.library)?.badgeName && (
    <Badge
      id="library"
      className={styles.libraryBadge}
      title={`This document is part of the ${documentVersionLibraryToDetails(documentVersion.library)?.name} library`}
    >
      <img src="/providence_small_nobackground.svg" alt="Providence Logo" height={24} />
      {documentVersionLibraryToDetails(documentVersion.library)?.badgeName}
    </Badge>
  );
  if (!isRoot) {
    if (!libraryBadge && !headerChrome) return null;
    return (
      <div className={clsx(styles.container, styles.notRoot, styles.newContainer)}>
        <div className={styles.headerRow}>
          {libraryBadge ? <div className={clsx(styles.badgeContainer)}>{libraryBadge}</div> : null}
          {headerChrome ? <div className={styles.pageChrome}>{headerChrome}</div> : null}
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${styles.newContainer}`}>
      <div className={styles.headerRow}>
        <h1 className={styles.tabHeading}>
          <div className={styles.iconFlags}>
            <IconsForDocumentFlags flags={documentVersion?.flags} />
          </div>
          <span>
            {documentVersion?.title} {tooltipComponent}
          </span>
        </h1>
        {headerChrome ? <div className={styles.pageChrome}>{headerChrome}</div> : null}
      </div>
      <Badges documentVersion={documentVersion} isFlipped={isFlipped} />
    </div>
  );
}

export function Badges({ documentVersion, isFlipped }: { documentVersion: DocumentVersionType; isFlipped: boolean }) {
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const isInternalOnly = isDocumentVersionInternalOnly(documentVersion);
  return (
    <div className={clsx(styles.badgeContainer, isInternalOnly && styles.internalOnlyBadgeContainer)}>
      {isInternalOnly && (
        <Badge id="internal-only" className={styles.internalBadge} title="This document is internal only">
          Internal Only
        </Badge>
      )}
      {documentVersionLibraryToDetails(documentVersion.library)?.badgeName && (
        <Badge
          id="library"
          className={styles.libraryBadge}
          title={`This document is part of the ${
            documentVersionLibraryToDetails(documentVersion.library)?.name
          } library`}
        >
          <img src="/providence_small_nobackground.svg" alt="Providence Logo" height={24} />
          {documentVersionLibraryToDetails(documentVersion.library)?.badgeName}
        </Badge>
      )}
      {/* Hide this for unauthenticated users. */}
      {documentVersion && (isFlipped || documentVersion?.state !== DocumentVersionState.PUBLISHED) && pearlUserInfo && (
        <Badge
          id="docversion-state"
          className={styles.stateBadge}
          data-doc-state={documentVersion.state}
          title={
            documentVersion.state === DocumentVersionState.PUBLISHED
              ? undefined
              : "Use the 'Published Only' toggle to see any Published version of this"
          }
        >
          {documentVersionStateToStr(documentVersion.state)}
        </Badge>
      )}
    </div>
  );
}
